import { useMemo } from "react";
import { sectionRegistry } from "@a2z/storefront-schemas";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { FieldRenderer } from "../fields/FieldRenderer";
import { evaluateShowIf, withFieldDefaults } from "../lib/showIf";
import type { ThemeDraftApi } from "../state/useThemeDraft";

/**
 * Right pane: the selected section's settings, rendered from its shared schema.
 *
 * Field ORDER, labels, help text, option sets and `showIf` gating all come from
 * `sectionRegistry`, so this pane automatically picks up any section or field
 * the 13-family workstream adds to `packages/storefront-schemas` — no admin
 * change needed. That is the payoff for building against the shared vocabulary
 * rather than a local mirror of it.
 */
export function SectionInspector({
  draft,
  lang,
}: {
  draft: ThemeDraftApi;
  lang: "ar" | "en";
}) {
  const t = useTranslations();
  const section = draft.selectedSection;

  const schema = section ? sectionRegistry.sections[section.type] : undefined;

  // `showIf` must be evaluated against defaults-overlaid values, not the sparse
  // stored settings: a field the author never touched still has an effective
  // value in the storefront, and gating on `undefined` would show or hide the
  // wrong controls.
  const visibleFields = useMemo(() => {
    if (!schema || !section) return [];
    const values = withFieldDefaults(
      schema.settings,
      (section.settings ?? {}) as Record<string, unknown>,
    );
    return schema.settings.filter((field) =>
      evaluateShowIf(field.showIf, values),
    );
  }, [schema, section]);

  if (!section) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <p className="max-w-[14rem] text-center text-[12px] text-[color:var(--text-muted)]">
          {t(keys.themes.studioSelectSection)}
        </p>
      </div>
    );
  }

  if (!schema) {
    // A section stored against a type this build's registry does not know —
    // e.g. json authored by a newer schema. Say so; do not render an empty
    // panel that reads as "this section has no settings".
    return (
      <div className="p-4">
        <p className="text-[12px] text-[color:var(--warning-700)]">
          {t(keys.themes.studioUnknownSection, { type: section.type })}
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-[color:var(--border-light)] px-3 py-2.5">
        <span className="text-[12px] font-bold text-[color:var(--text-primary)]">
          {schema.name?.[lang] ?? schema.name?.en ?? section.type}
        </span>
      </div>
      <div className="flex-1 overflow-auto p-3">
        {visibleFields.length === 0 ? (
          <p className="text-[12px] text-[color:var(--text-muted)]">
            {t(keys.themes.studioNoFields)}
          </p>
        ) : (
          <div className="flex flex-col gap-4">
            {visibleFields.map((field) => (
              <FieldRenderer
                key={field.id}
                field={field}
                lang={lang}
                value={(section.settings as Record<string, unknown>)?.[field.id]}
                onChange={(value) =>
                  draft.updateSectionSettings(section.id, { [field.id]: value })
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
